import Vue from 'vue'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import store from './store'
import vuetify from './plugins/vuetify';
import swalPlugin from './plugins/swal';
import './plugins/toast'; // Vue Toastification для стекирующихся уведомлений
import './styles/toast-notifications.scss'; // Стили для toast-уведомлений
// @ts-ignore
import QueryHelper from "./mixins/QueryHelper.js";
// @ts-ignore
import NotificationsMixin from "./mixins/NotificationsMixin.js";
import VueMask from 'v-mask';
import * as Sentry from "@sentry/vue";
import errorHandler from './services/errorHandler';

// @ts-ignore
Vue.use(VueMask)
Vue.use(swalPlugin)
Vue.config.productionTip = false

// Скрываем Vue Devtools сообщение в консоли (только для dev)
if (process.env.NODE_ENV === 'development') {
  Vue.config.devtools = true; // Оставляем devtools работающими
  // Фильтруем только сообщение о devtools
  const originalLog = console.log;
  console.log = function(...args) {
    if (args[0]?.includes?.('Vue Devtools')) return;
    originalLog.apply(console, args);
  };
}

Vue.mixin(QueryHelper);
Vue.mixin(NotificationsMixin);
Vue.mixin({
    methods: {
        copyObject(object) {
            return JSON.parse(JSON.stringify(object))
        }
    }
})

Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DNS,
    environment: process.env.VUE_APP_ENVIRONMENT,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Инициализируем errorHandler перед созданием Vue instance
errorHandler.init().then(() => {
    const app = new Vue({
        router,
        store,
        vuetify,
        render: h => h(App)
    }).$mount('#app')

    // Делаем $notify доступным в window для registerServiceWorker
    ;(window as any).$notify = (app as any).$notify?.bind(app)
    
    // Скрываем SplashScreen после загрузки Vue приложения
    // Увеличена задержка, чтобы избежать конфликта с hideSplashScreen из index.html
    if (typeof (window as any).hideSplashScreen === 'function') {
        setTimeout(() => {
            (window as any).hideSplashScreen();
        }, 600); // Увеличено с 100ms до 600ms, чтобы index.html успел скрыть splash первым
    }
    
    // 🔍 ДИАГНОСТИКА: Отслеживаем модальные окна (включается ТОЛЬКО при window.__MODAL_DEBUG__ = true)
    const MODAL_DEBUG = (window as any).__MODAL_DEBUG__ === true;
    if (MODAL_DEBUG) {
        const diagnoseModal = (dialog: Element) => {
            setTimeout(() => {
                console.group('🔍 ДИАГНОСТИКА МОДАЛЬНОГО ОКНА (Глобальная)');
                
                const htmlEl = dialog as HTMLElement;
                const styles = window.getComputedStyle(htmlEl);
                
                console.log('📦 .v-dialog элемент:', htmlEl);
                console.log('  Классы:', htmlEl.className);
                console.log('  Background:', styles.backgroundColor, styles.background);
                console.log('  Border:', styles.border, 'Width:', styles.borderWidth);
                console.log('  Box-shadow:', styles.boxShadow);
                console.log('  Outline:', styles.outline, 'Width:', styles.outlineWidth);
                console.log('  Padding:', styles.padding);
                console.log('  Margin:', styles.margin);
                
                // Проверяем родительские элементы
                let parent = htmlEl.parentElement;
                let level = 1;
                while (parent && level <= 3) {
                    const parentStyles = window.getComputedStyle(parent);
                    if (parentStyles.border && parentStyles.border !== '0px none rgb(0, 0, 0)') {
                        console.warn(`⚠️ Родитель #${level} имеет border:`, parent, parentStyles.border);
                    }
                    if (parentStyles.boxShadow && parentStyles.boxShadow !== 'none') {
                        console.warn(`⚠️ Родитель #${level} имеет box-shadow:`, parent, parentStyles.boxShadow);
                    }
                    parent = parent.parentElement;
                    level++;
                }
                
                // Проверяем overlay
                const overlay = document.querySelector('.v-overlay.v-overlay--active');
                if (overlay) {
                    const overlayStyles = window.getComputedStyle(overlay);
                    console.log('📦 .v-overlay элемент:', overlay);
                    console.log('  Background:', overlayStyles.backgroundColor);
                    console.log('  Border:', overlayStyles.border);
                    console.log('  Box-shadow:', overlayStyles.boxShadow);
                }
                
                console.groupEnd();
            }, 300);
        };
        
        // Отслеживаем появление v-dialog с классом --active (с защитой от повторной диагностики)
        const diagnosedDialogs = new WeakSet<Element>();
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const el = node as Element;
                        if (el.classList?.contains('v-dialog') && el.classList?.contains('v-dialog--active')) {
                            if (!diagnosedDialogs.has(el)) {
                                diagnosedDialogs.add(el);
                                diagnoseModal(el);
                            }
                        }
                    }
                });
                
                // Также отслеживаем изменение классов
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const target = mutation.target as Element;
                    if (target.classList?.contains('v-dialog') && target.classList?.contains('v-dialog--active')) {
                        if (!diagnosedDialogs.has(target)) {
                            diagnosedDialogs.add(target);
                            diagnoseModal(target);
                        }
                    }
                }
            });
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class']
        });
        
        // Экспортируем функцию для ручного вызова из консоли
        (window as any).diagnoseModal = () => {
            const dialog = document.querySelector('.v-dialog.v-dialog--active');
            if (dialog) {
                diagnoseModal(dialog);
            } else {
                console.warn('⚠️ Нет открытых модальных окон');
            }
        };
        
        // 🔍 ДЕТАЛЬНАЯ ДИАГНОСТИКА РАМКИ: анализирует все источники box-shadow
        (window as any).diagnoseModalFrame = () => {
            const dialog = document.querySelector('.v-dialog.v-dialog--active');
            if (!dialog) {
                console.warn('⚠️ Нет открытых модальных окон');
                return;
            }
            
            const htmlEl = dialog as HTMLElement;
            
            console.group('🔍 ДЕТАЛЬНАЯ ДИАГНОСТИКА РАМКИ МОДАЛЬНОГО ОКНА');
            
            // 1. Анализируем сам .v-dialog
            console.group('📦 1. Элемент .v-dialog');
            console.log('Элемент:', htmlEl);
            console.log('Классы:', htmlEl.className);
            
            const computedStyles = window.getComputedStyle(htmlEl);
            console.log('Computed box-shadow:', computedStyles.boxShadow);
            console.log('Computed border:', computedStyles.border, 'Width:', computedStyles.borderWidth);
            console.log('Computed outline:', computedStyles.outline, 'Width:', computedStyles.outlineWidth);
            console.log('Computed background:', computedStyles.backgroundColor);
            
            // Проверяем inline стили
            console.group('📝 Inline стили:');
            if (htmlEl.style.length > 0) {
                console.log('Inline стили присутствуют:', htmlEl.style.cssText);
                console.log('  box-shadow:', htmlEl.style.boxShadow || 'НЕТ');
                console.log('  border:', htmlEl.style.border || 'НЕТ');
                console.log('  outline:', htmlEl.style.outline || 'НЕТ');
            } else {
                console.log('Inline стили отсутствуют');
            }
            console.groupEnd();
            
            // Проверяем CSS правила из stylesheet
            console.group('📄 CSS правила из stylesheets:');
            const sheet = Array.from(document.styleSheets);
            let foundRules = false;
            sheet.forEach((styleSheet, sheetIndex) => {
                try {
                    const rules = Array.from(styleSheet.cssRules || []);
                    rules.forEach((rule, ruleIndex) => {
                        if (rule instanceof CSSStyleRule) {
                            if (htmlEl.matches(rule.selectorText)) {
                                foundRules = true;
                                console.log(`Stylesheet #${sheetIndex}, Rule #${ruleIndex}:`, rule.selectorText);
                                console.log('  box-shadow:', rule.style.boxShadow || 'НЕТ');
                                console.log('  border:', rule.style.border || 'НЕТ');
                                console.log('  outline:', rule.style.outline || 'НЕТ');
                                console.log('  Полное правило:', rule.cssText);
                            }
                        }
                    });
                } catch (e) {
                    // Игнорируем ошибки доступа к CORS stylesheets
                }
            });
            if (!foundRules) {
                console.log('Прямых правил для .v-dialog не найдено');
            }
            console.groupEnd();
            
            console.groupEnd();
            
            // 2. Анализируем родительские элементы
            console.group('📦 2. Родительские элементы');
            let parent = htmlEl.parentElement;
            let level = 1;
            while (parent && level <= 5) {
                const parentStyles = window.getComputedStyle(parent);
                const hasShadow = parentStyles.boxShadow && parentStyles.boxShadow !== 'none';
                const hasBorder = parentStyles.borderWidth && parseInt(parentStyles.borderWidth) > 0;
                
                if (hasShadow || hasBorder) {
                    console.warn(`⚠️ Родитель #${level} (${parent.tagName}.${parent.className}):`);
                    console.log('  box-shadow:', parentStyles.boxShadow);
                    console.log('  border:', parentStyles.border);
                }
                parent = parent.parentElement;
                level++;
            }
            console.groupEnd();
            
            // 3. Проверяем .v-card внутри dialog
            console.group('📦 3. Элементы .v-card внутри dialog');
            const cards = htmlEl.querySelectorAll('.v-card, .editor-modal');
            cards.forEach((card, index) => {
                const cardEl = card as HTMLElement;
                const cardStyles = window.getComputedStyle(cardEl);
                console.log(`Card #${index} (${cardEl.className}):`);
                console.log('  box-shadow:', cardStyles.boxShadow);
                console.log('  border:', cardStyles.border, 'Width:', cardStyles.borderWidth);
                if (cardEl.style.length > 0) {
                    console.log('  Inline стили:', cardEl.style.cssText);
                }
            });
            console.groupEnd();
            
            // 4. Проверяем .v-dialog__content
            console.group('📦 4. Элемент .v-dialog__content');
            const content = htmlEl.querySelector('.v-dialog__content');
            if (content) {
                const contentEl = content as HTMLElement;
                const contentStyles = window.getComputedStyle(contentEl);
                console.log('Элемент:', contentEl);
                console.log('  box-shadow:', contentStyles.boxShadow);
                console.log('  border:', contentStyles.border);
                console.log('  Inline стили:', contentEl.style.cssText || 'НЕТ');
            }
            console.groupEnd();
            
            // 5. Проверяем overlay
            console.group('📦 5. Overlay (НЕ ТРОГАЕМ!)');
            const overlay = document.querySelector('.v-overlay.v-overlay--active');
            if (overlay) {
                const overlayEl = overlay as HTMLElement;
                const overlayStyles = window.getComputedStyle(overlayEl);
                console.log('Элемент:', overlayEl);
                console.log('  background-color:', overlayStyles.backgroundColor);
                console.log('  opacity:', overlayStyles.opacity);
            } else {
                console.log('Overlay не найден');
            }
            console.groupEnd();
            
            // 6. ПРЕДЛОЖЕНИЕ: временно убрать box-shadow для проверки
            console.group('🧪 ТЕСТ: Временное удаление box-shadow');
            console.log('Для проверки гипотезы можно временно удалить box-shadow:');
            console.log('  const dialog = document.querySelector(".v-dialog.v-dialog--active");');
            console.log('  if (dialog) dialog.style.setProperty("box-shadow", "none", "important");');
            console.log('');
            console.log('Чтобы вернуть обратно:');
            console.log('  if (dialog) dialog.style.removeProperty("box-shadow");');
            console.groupEnd();
            
            console.groupEnd();
        };
        
        console.log('🔍 Диагностические функции:');
        console.log('  - window.diagnoseModal() - общая диагностика');
        console.log('  - window.diagnoseModalFrame() - детальная диагностика рамки');
        // ФИКС: Минимальный — только класс для диалога, overlay не трогаем (пусть управляет Vuetify)
        const removeModalFrame = () => {
            if (window.innerWidth <= 1024) {
                requestAnimationFrame(() => {
                    // Добавляем класс для .v-dialog, CSS в modals-common.scss снимет рамку/тень
                    const dialogs = document.querySelectorAll('.v-dialog.v-dialog--active');
                    dialogs.forEach((dialog: Element) => {
                        (dialog as HTMLElement).classList.add('no-modal-frame');
                    });
                });
            }
        };
        
        // Debounce для оптимизации
        let frameFixTimeout: number | null = null;
        const debouncedRemoveFrame = () => {
            if (frameFixTimeout) {
                clearTimeout(frameFixTimeout);
            }
            frameFixTimeout = window.setTimeout(removeModalFrame, 50);
        };
        
        // Применяем фикс при открытии/активации модального окна
        const frameObserver = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.type === 'childList') {
                    // реагируем только на добавление .v-dialog
                    const added = Array.from(m.addedNodes).some(n => (n as Element)?.classList?.contains?.('v-dialog'));
                    if (added) debouncedRemoveFrame();
                } else if (m.type === 'attributes') {
                    const target = m.target as Element;
                    if (target.classList?.contains('v-dialog') && target.classList?.contains('v-dialog--active')) {
                        debouncedRemoveFrame();
                    }
                }
            }
        });
        
        frameObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style']
        });
        
        // Применяем сразу при загрузке
        setTimeout(removeModalFrame, 100);
        
        console.log('🔍 Диагностика модальных окон включена (window.__MODAL_DEBUG__ = true).');
    }

    // Всегда экспортируем упрощенную диагностику рамки без флагов
    (window as any).diagnoseModalFrame = () => {
        const dialog = document.querySelector('.v-dialog.v-dialog--active');
        if (!dialog) {
            console.warn('⚠️ Нет открытых модальных окон');
            return;
        }
        const el = dialog as HTMLElement;
        const cs = window.getComputedStyle(el);
        console.group('🔍 Быстрая диагностика рамки (.v-dialog)');
        console.log('box-shadow:', cs.boxShadow);
        console.log('border:', cs.border, 'width:', cs.borderWidth);
        console.log('outline:', cs.outline, 'width:', cs.outlineWidth);
        console.log('inline box-shadow:', el.style.boxShadow || 'нет');
        console.groupEnd();
        console.log('🧪 Для временной проверки можно выполнить:');
        console.log('  const d = document.querySelector(".v-dialog.v-dialog--active"); if (d) d.style.setProperty("box-shadow", "none", "important")');
    };

    // Диагностика горизонтального/нижнего скролла в активном модальном окне
    (window as any).diagnoseModalScroll = () => {
        const dialog = document.querySelector('.v-dialog.v-dialog--active');
        if (!dialog) {
            console.warn('⚠️ Нет открытых модальных окон');
            return;
        }
        const toPath = (el: Element) => {
            const parts: string[] = [];
            let node: Element | null = el;
            while (node && node !== document.body) {
                const name = node.tagName.toLowerCase();
                const cls = (node.className || '').toString().split(' ').filter(Boolean)[0] || '';
                parts.unshift(name + (cls ? '.' + cls : ''));
                node = node.parentElement;
            }
            return parts.join(' > ');
        };
        const offenders: Array<{el: HTMLElement, overflowX: string, scrollW: number, clientW: number, totalW: number}> = [];
        (dialog as HTMLElement).querySelectorAll('*').forEach((n) => {
            const el = n as HTMLElement;
            const cs = window.getComputedStyle(el);
            const sw = el.scrollWidth;
            const cw = el.clientWidth;
            const rect = el.getBoundingClientRect();
            const totalW = rect.width;
            if (sw > cw + 1 || (cs.overflowX !== 'hidden' && totalW > cw + 1)) {
                offenders.push({ el, overflowX: cs.overflowX, scrollW: sw, clientW: cw, totalW });
            }
        });
        console.group('🔍 Диагностика нижнего скролла в модальном окне');
        const dlg = dialog as HTMLElement;
        console.log('Dialog width/scroll:', dlg.clientWidth, dlg.scrollWidth);
        if (offenders.length === 0) {
            console.log('✅ Явных источников горизонтального скролла не найдено');
        } else {
            offenders.sort((a, b) => (b.scrollW - b.clientW) - (a.scrollW - a.clientW));
            offenders.slice(0, 10).forEach((o, i) => {
                console.group(`#${i} ${toPath(o.el)}`);
                console.log('overflow-x:', o.overflowX);
                console.log('clientWidth:', o.clientW, 'scrollWidth:', o.scrollW, 'bbox width:', o.totalW);
                console.log('classes:', o.el.className);
                console.groupEnd();
            });
            const worst = offenders[0];
            if (worst) {
                (window as any).__modalOffender = worst.el;
                console.warn('⚠️ Наиболее вероятный виновник сохранён как window.__modalOffender');
            }
        }
        console.groupEnd();
        console.log('Вспомогательные команды:');
        console.log('  window.__modalOffender && (window.__modalOffender.style.outline = "2px solid red")');
        console.log('  // Быстрый тест: скрыть горизонтальный скролл у карточки');
        console.log('  const card = document.querySelector(".v-dialog.v-dialog--active .v-card"); if (card) card.style.overflowX = "hidden"');
    };
}).catch((error) => {
    console.error('[Main] Failed to initialize ErrorHandler:', error)
    
    // Все равно создаем приложение с fallback сообщениями
    const app = new Vue({
        router,
        store,
        vuetify,
        render: h => h(App)
    }).$mount('#app')
    
    ;(window as any).$notify = (app as any).$notify?.bind(app)
    
    // Скрываем SplashScreen даже при ошибке инициализации
    if (typeof (window as any).hideSplashScreen === 'function') {
        setTimeout(() => {
            (window as any).hideSplashScreen();
        }, 100);
    }
})